/**
 * Provider Form Hook
 * 
 * Custom hook that encapsulates provider form logic, data transformations,
 * and change detection. This hook can be reused across different provider
 * management components.
 * 
 * @module useProviderForm
 */

import { useState, useCallback, useEffect } from 'react';
import ProviderTransformationService from '../services/provider-transformation.service';
import ChangeDetectionService from '../services/change-detection.service';

/**
 * Custom hook for provider form management
 * 
 * @param {Object} provider - Provider data for editing (null for create)
 * @param {Function} onSubmit - Submit callback function
 * @returns {Object} Form management utilities
 */
export const useProviderForm = (provider, onSubmit) => {
  const [transformedInitialValues, setTransformedInitialValues] = useState(null);
  const [originalApiData, setOriginalApiData] = useState(null);
  const [facilitiesDepartments, setFacilitiesDepartments] = useState({});
  const [loadingStates, setLoadingStates] = useState({});

  /**
   * Transform form data and detect changes for submission
   */
  const handleFormSubmit = useCallback(async (formData) => {
    try {
      // Transform form data to API format
      const currentApiData = ProviderTransformationService.transformFormToAPI(formData);
      
      let submitData;
      
      if (provider && originalApiData) {
        // Edit mode: detect and send only changes
        const editableFields = ['fullName', 'npi', 'taxId', 'specialties', 'affiliations'];
        const changedFields = ChangeDetectionService.detectChanges(originalApiData, currentApiData, editableFields);
        
        if (!ChangeDetectionService.hasChanges(originalApiData, currentApiData, editableFields)) {
          return; // No changes, skip update
        }
        
        submitData = changedFields;
      } else {
        // Create mode: send all data
        submitData = currentApiData;
      }
      
      await onSubmit(submitData);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      throw error;
    }
  }, [onSubmit, provider, originalApiData]);

  /**
   * Load and transform provider data for editing
   */
  useEffect(() => {
    const loadProviderData = async () => {
      if (provider) {
        try {
          const transformed = await ProviderTransformationService.transformAPIToForm(
            provider, 
            setFacilitiesDepartments
          );
          setTransformedInitialValues(transformed);
          
          // Store original API data for change detection
          const originalApi = ProviderTransformationService.transformFormToAPI(transformed);
          setOriginalApiData(originalApi);
        } catch (error) {
          console.error('Error loading provider data:', error);
          // Fallback to original provider data with minimal structure
          const fallbackData = {
            ...provider,
            affiliations: [{ facilityId: '', role: [] }]
          };
          setTransformedInitialValues(fallbackData);
          
          const originalApi = ProviderTransformationService.transformFormToAPI(fallbackData);
          setOriginalApiData(originalApi);
        }
      } else {
        // For new providers, use default structure
        const defaultData = {
          fullName: '',
          npi: '',
          taxId: '',
          specialties: [],
          affiliations: [{ facilityId: '', role: [] }]
        };
        setTransformedInitialValues(defaultData);
        setOriginalApiData(null);
      }
    };

    loadProviderData();
  }, [provider]);

  /**
   * Get initial form values with fallback
   */
  const initialFormValues = transformedInitialValues || {
    fullName: '',
    npi: '',
    taxId: '',
    specialties: [],
    affiliations: [{ facilityId: '', role: [] }]
  };

  return {
    initialFormValues,
    facilitiesDepartments,
    setFacilitiesDepartments,
    loadingStates,
    setLoadingStates,
    handleFormSubmit,
    transformedInitialValues
  };
};

export default useProviderForm;
